import { BookingPageErrorState } from 'bookwithme-shared';
import { DatapointStatus, PerformanceDatapoint, logUsage } from 'owa-analytics';
import { setBookingPageErrorState } from '../actions/setBookingPageErrorState';
/* eslint-disable-next-line @typescript-eslint/no-restricted-imports  -- (https://aka.ms/OWALintWiki)
 * Baseline. Do not copy and paste"
 *	> '../index' import is restricted from being used. */
import { lazySetBookingPageErrorState } from '../index';
import getBookingMailboxGuid from '../selectors/getBookingMailboxGuid';
import getBookingMeetingTypeId from '../selectors/getBookingMeetingTypeId';
import getCustomerInfoTenantName from '../selectors/getCustomerInfoTenantName';
import { COOKIE_REFRESH } from '../store/schema/BookingStore';
import type { PersonalBookingData } from '../store/schema/PersonalBookingData';
import { getPageDataLoadOrchestrator } from '../orchestrators/getPageDataLoadOrchestrator';
import getPageOrigin from '../selectors/getPageOrigin';

const GUID_NO_DASH_REGEX = /^[0-9a-f]{32}$/;

export async function getBookingSessionData(
    bookingMailbox: string,
    entryPoint?: string,
    bookingServiceId?: string,
    bookingId?: string
): Promise<PersonalBookingData | null> {
    // Added Eventify=1 to URL query for substrate to identify the client and go through the PBP authenticated path,
    // when session is not anonymous: cookie with anchor mailbox, user has PUId, etc.
    // If want to force the not authenticated path (fallback to email code verification) even when session is authenticated can be done by sending w/o eventify=1.
    // For a specific bookingServiceId, we don't use pbp/s/${bookingServiceId} because: 1) /pbp/s/${bookingServiceId} returns all services, 2) ?eventify=1 not yet supported.

    logUsage('BwmAttendeePageVisited', {
        pageOrigin: getPageOrigin(),
        entryPoint: entryPoint ?? '',
    });

    const perfDatapoint = new PerformanceDatapoint('getBookingSessionData');

    // Need to support both @ in the address and the encoded value %40 that sometimes is provided.
    const organizerMailboxSplit =
        bookingMailbox.indexOf('@') > -1 ? bookingMailbox.split('@') : bookingMailbox.split('%40');
    const organizerGuid = organizerMailboxSplit[0];
    if (!isGuid(organizerGuid)) {
        lazySetBookingPageErrorState.importAndExecute(BookingPageErrorState.InvalidSignedUrl);
        return null;
    }
    try {
        const responseB2 = await getPageDataLoadOrchestrator(
            bookingMailbox,
            bookingServiceId,
            bookingId
        );

        if (!responseB2) {
            if (entryPoint != COOKIE_REFRESH) {
                perfDatapoint.addCustomData({
                    entryPoint: entryPoint ?? '',
                    bookingId,
                    exception: BookingPageErrorState.NoBookingPageDataPayload,
                    tenantName: getCustomerInfoTenantName(),
                    bookingMailboxGuid: getBookingMailboxGuid(organizerGuid),
                    meetingTypeId: getBookingMeetingTypeId(),
                });
                perfDatapoint.endWithError(DatapointStatus.ServerError);
            } else {
                // Invalidate the datapoint so it doesn't get reported anywhere
                perfDatapoint.invalidate();
            }
            lazySetBookingPageErrorState.importAndExecute(
                BookingPageErrorState.NoBookingPageDataPayload
            );
            return null;
        }
        // Should set error page only if the meeting type was private, which is indicated by displayedServiceNotFound being false
        if (responseB2.Services.length == 0) {
            setBookingPageErrorState(BookingPageErrorState.NoPublicMeetingTypes);
            return null;
        }

        perfDatapoint.addCustomData({
            bookingId,
            bookingServiceId,
            entryPoint: entryPoint ?? '',
            tenantName: getCustomerInfoTenantName(),
            bookingMailboxGuid: getBookingMailboxGuid(organizerGuid),
            meetingTypeId: getBookingMeetingTypeId(),
        });
        perfDatapoint.end();

        return responseB2;
    } catch (error) {
        if (entryPoint != COOKIE_REFRESH) {
            perfDatapoint.addCustomData({
                entryPoint: entryPoint ?? '',
                bookingId,
                exception: BookingPageErrorState.NoBookingPageDataPayload,
                tenantName: getCustomerInfoTenantName(),
            });
            perfDatapoint.endWithError(DatapointStatus.ServerError, error);
        } else {
            // Invalidate the datapoint so it doesn't get reported anywhere
            perfDatapoint.invalidate();
        }
        lazySetBookingPageErrorState.importAndExecute(
            BookingPageErrorState.NoBookingPageDataPayload
        );
        return null;
    }
}

/*
 * Check if the string is a guid w/o dashes used in the obscured address scenario.
 */
function isGuid(str: string): boolean {
    str = str.replace(/mbx%3A|-/g, '');
    return str.length === 32 && GUID_NO_DASH_REGEX.test(str);
}
